/**
 * MedicationListPDF — branded @react-pdf/renderer component
 * Mirrors the reference design exactly: cover page → medication cards → signature block.
 */
import React from "react";
import {
  Document,
  Page,
  View,
  Text,
  StyleSheet,
  Image,
} from "@react-pdf/renderer";
import moment from "moment";
import { BRAND_COLORS } from "../BrandedReport/reactPdfStyles";

/* ─── colour palette ─── */
const C = {
  ...BRAND_COLORS,
  dark: "#1D2939",
  green: "#2E7D32",
  greenBg: "#E8F5E9",
  greenBorder: "#A5D6A7",
  amberBg: "#FFF8E1",
  amberDark: "#F57F17",
  amberBorder: "#FFE082",
  greyBg: "#F5F5F5",
  greyMedBg: "#EEEEEE",
  greyDark: "#757575",
  greyBorder: "#BDBDBD",
  redText: "#C62828",
  warningBg: "#FFF8E1",
  warningBorder: "#FFE082",
  cardBorder: "#E0E0E0",
  detailBg: "#FAFAFA",
  lightLine: "#E5E7EB",
};

/* ─── styles ─── */
const s = StyleSheet.create({
  page: { padding: 36, fontFamily: "Helvetica", fontSize: 9, color: "#1C1917", backgroundColor: "#F5F3EE" },

  /* ── COVER — dark header block ── */
  coverHeader: {
    backgroundColor: C.dark,
    margin: -36,
    marginBottom: 0,
    padding: "28px 36px 24px 36px",
  },
  coverTopRow: { flexDirection: "row", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 18 },
  coverBrand: { flexDirection: "row", alignItems: "center", gap: 8 },
  coverLogoCircle: { width: 36, height: 36, borderRadius: 18, backgroundColor: C.amber, justifyContent: "center", alignItems: "center" },
  coverLogoImg: { width: 36, height: 36, borderRadius: 18, objectFit: "cover" },
  coverLogoText: { color: C.white, fontWeight: "bold", fontSize: 13 },
  coverBrandName: { fontSize: 13, fontWeight: "bold", color: C.white },
  coverBrandSub: { fontSize: 8, color: "#9DA4AE", marginTop: 1 },
  coverGenerated: { textAlign: "right" },
  coverGenTitle: { fontSize: 9, fontWeight: "bold", color: "#D0D5DD" },
  coverGenDate: { fontSize: 8, color: "#9DA4AE", marginTop: 2 },
  coverBadge: { backgroundColor: C.amber, borderRadius: 4, paddingVertical: 4, paddingHorizontal: 12, alignSelf: "flex-start", marginBottom: 12 },
  coverBadgeText: { color: C.white, fontSize: 9, fontWeight: "bold" },
  coverClientName: { fontSize: 28, fontWeight: "bold", color: C.white, marginBottom: 4 },
  coverSubtitle: { fontSize: 10, color: "#9DA4AE", marginBottom: 0 },

  /* ── COVER — white body ── */
  coverBody: { paddingTop: 20 },
  infoRow: { flexDirection: "row", marginBottom: 16 },
  infoCol: { width: "50%" },
  infoSectionTitle: { fontSize: 8, fontWeight: "bold", textTransform: "uppercase", letterSpacing: 0.6, color: C.labelGrey, marginBottom: 8, borderBottomWidth: 0.5, borderBottomColor: C.lightBorder, paddingBottom: 4 },
  infoLine: { flexDirection: "row", marginBottom: 5, alignItems: "flex-start" },
  infoLabel: { width: 110, fontSize: 8, color: C.labelGrey },
  infoValue: { fontSize: 9, color: C.bodyText, fontWeight: "medium", flex: 1 },
  infoValueRed: { fontSize: 9, color: C.redText, fontWeight: "bold", flex: 1 },

  /* summary cards */
  summarySection: { borderBottomWidth: 0.5, borderBottomColor: C.lightBorder, paddingBottom: 12, marginBottom: 12 },
  summaryTitle: { fontSize: 8, fontWeight: "bold", textTransform: "uppercase", letterSpacing: 0.6, color: C.labelGrey, marginBottom: 8 },
  summaryRow: { flexDirection: "row", gap: 12 },
  summaryCard: { width: 80, borderWidth: 0.5, borderColor: C.cardBorder, borderRadius: 4, padding: 8, alignItems: "center" },
  summaryNum: { fontSize: 22, fontWeight: "bold", marginBottom: 2 },
  summarySub: { fontSize: 7, fontWeight: "bold", textTransform: "uppercase", letterSpacing: 0.4, color: C.labelGrey },

  /* legend */
  legendTitle: { fontSize: 8, fontWeight: "bold", textTransform: "uppercase", letterSpacing: 0.6, color: C.labelGrey, marginBottom: 8 },
  legendRow: { flexDirection: "row", gap: 24, marginBottom: 4 },
  legendItem: { flexDirection: "row", alignItems: "center", gap: 6 },
  legendDot: { width: 8, height: 8, borderRadius: 4 },
  legendText: { fontSize: 8, color: C.bodyText },

  /* ── CONTINUATION HEADER (pages 2+) ── */
  contHeader: { flexDirection: "row", justifyContent: "space-between", alignItems: "flex-start", borderBottomWidth: 1.5, borderBottomColor: C.dark, paddingBottom: 8, marginBottom: 14 },
  contLeft: { flexDirection: "row", alignItems: "center", gap: 8 },
  contLogo: { width: 28, height: 28, borderRadius: 14, backgroundColor: C.amber, justifyContent: "center", alignItems: "center" },
  contLogoImg: { width: 28, height: 28, borderRadius: 14, objectFit: "cover" },
  contLogoText: { color: C.white, fontWeight: "bold", fontSize: 12 },
  contFacility: { fontSize: 10, fontWeight: "bold", color: C.bodyText },
  contSub: { fontSize: 7, color: C.muted, marginTop: 1 },
  contRight: { textAlign: "right" },
  contClient: { fontSize: 11, fontWeight: "bold", color: C.bodyText },
  contMeta: { fontSize: 7, color: C.muted, marginTop: 2 },

  /* ── SECTION HEAD — small uppercase label + count badge ── */
  sectionHead: { flexDirection: "row", alignItems: "center", justifyContent: "space-between", marginBottom: 10, marginTop: 4 },
  sectionTitle: { fontSize: 8, fontWeight: "bold", textTransform: "uppercase", letterSpacing: 0.8, color: "#9C9690" },
  sectionBadge: { fontSize: 8, fontWeight: "bold", paddingVertical: 2, paddingHorizontal: 10, borderRadius: 12 },
  sectionBadgeActive: { backgroundColor: "#ECFDF5", color: "#065F46" },
  sectionBadgeDisc: { backgroundColor: "#F3F4F6", color: "#6B7280" },

  /* Divider with centered label */
  divider: { marginVertical: 20, borderBottomWidth: 1, borderBottomColor: "#E4E0D8" },
  dividerLabel: { fontSize: 8, fontWeight: "bold", textTransform: "uppercase", letterSpacing: 0.6, color: "#9C9690", textAlign: "center", marginBottom: 10 },

  /* ═══ MEDICATION CARD — single-row horizontal layout ═══ */
  medCard: {
    backgroundColor: C.white,
    borderWidth: 1,
    borderColor: "#E4E0D8",
    borderRadius: 10,
    marginBottom: 8,
    overflow: "hidden",
    flexDirection: "row",
    alignItems: "center",
  },
  /* Colored left stripe */
  medStripe: { width: 4, alignSelf: "stretch" },
  stripeActive: { backgroundColor: "#10B981" },
  stripePRN: { backgroundColor: "#EA580C" },
  stripeDisc: { backgroundColor: "#D1D5DB" },

  /* Row body — everything right of stripe */
  medRow: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    padding: "10px 14px 10px 10px",
    gap: 8,
  },

  /* Green/grey dot */
  medDot: { width: 7, height: 7, borderRadius: 4, flexShrink: 0 },

  /* Name block (med name + dosage stacked) */
  medNameBlock: { width: 140, flexShrink: 0 },
  medName: { fontSize: 11, fontWeight: "bold", color: "#1C1917", letterSpacing: -0.2 },
  medDosage: { fontSize: 7.5, color: "#6B6560", marginTop: 1 },
  discMedName: { fontSize: 11, fontWeight: "bold", color: "#9CA3AF", letterSpacing: -0.2 },

  /* Type badge */
  medTypeBadge: { borderRadius: 12, paddingVertical: 2, paddingHorizontal: 8, alignSelf: "center" },
  medTypeBadgeText: { fontSize: 7, fontWeight: "bold" },

  /* Info columns (DOSE, ROUTE, START) */
  medInfoCol: { width: 55, flexShrink: 0 },
  medInfoLabel: { fontSize: 6, fontWeight: "bold", textTransform: "uppercase", letterSpacing: 0.4, color: "#9C9690", marginBottom: 1 },
  medInfoValue: { fontSize: 9, color: "#1C1917", fontWeight: "medium" },

  /* Disc-specific info */
  medInfoValueRed: { fontSize: 9, color: "#DC2626", fontWeight: "bold" },
  medInfoValueMuted: { fontSize: 9, color: "#9C9690" },

  /* Frequency / status badge (right end) */
  freqBadge: { borderRadius: 12, paddingVertical: 4, paddingHorizontal: 10, borderWidth: 1 },
  freqBadgeActive: { backgroundColor: "#FEF3D7", borderColor: "#E6A817" },
  freqBadgeDisc: { backgroundColor: "#F3F4F6", borderColor: "#D1D5DB" },
  freqBadgeText: { fontSize: 8, fontWeight: "bold", color: "#92680E" },
  freqBadgeTextDisc: { fontSize: 8, fontWeight: "bold", color: "#6B7280" },

  /* Spacer to push freq badge right */
  medSpacer: { flex: 1 },

  /* ── SIGNATURE BLOCK ── */
  signatureSection: { marginTop: 24, borderTopWidth: 0.5, borderTopColor: C.lightBorder, paddingTop: 16 },
  signatureRow: { flexDirection: "row", gap: 20 },
  signatureBlock: { flex: 1 },
  signatureLabel: { fontSize: 7, fontWeight: "bold", textTransform: "uppercase", letterSpacing: 0.5, color: C.labelGrey, marginBottom: 20 },
  signatureLine: { borderBottomWidth: 0.5, borderBottomColor: C.bodyText, marginBottom: 4 },
  signatureHelper: { fontSize: 7, color: C.muted },

  /* ── FOOTER ── */
  footer: { position: "absolute", bottom: 20, left: 36, right: 36, flexDirection: "row", justifyContent: "space-between", borderTopWidth: 0.5, borderTopColor: C.lightBorder, paddingTop: 6 },
  footerLeft: {},
  footerRight: { textAlign: "right" },
  footerText: { fontSize: 7, color: C.footerGrey },
  footerBold: { fontSize: 7, color: C.footerGrey, fontWeight: "bold" },
});

/* ─── helpers ─── */
const getStatusInfo = (med) => {
  if (med.discontinued) return {
    label: "Discontinued",
    badgeStyle: s.statusBadgeDisc,
    textStyle: s.statusTextDisc,
    dotColor: "#9E9E9E",
    since: med.discontinuedOn ? `Ended ${moment(med.discontinuedOn).format("MM/DD/YYYY")}` : "Ended",
  };
  if ((med.frequency || "").toLowerCase().includes("prn") || (med.frequency || "").toLowerCase().includes("as needed")) return {
    label: "PRN",
    badgeStyle: s.statusBadgePRN,
    textStyle: s.statusTextPRN,
    dotColor: C.amberDark,
    since: med.medicationDate ? `Since ${moment(med.medicationDate).format("MM/DD/YYYY")}` : "",
  };
  return {
    label: "Active",
    badgeStyle: s.statusBadgeActive,
    textStyle: s.statusTextActive,
    dotColor: C.green,
    since: med.medicationDate ? `Since ${moment(med.medicationDate).format("MM/DD/YYYY")}` : "",
  };
};

const buildInstructions = (med) => {
  // Build a human-readable instruction string from medication fields
  const parts = [];
  if (med.medicationadministered && med.medicationadministered !== med.dosage) {
    return med.medicationadministered;
  }
  const dose = [med.dosage, med.unit, med.dosageForm].filter(Boolean).join(" ");
  if (dose) parts.push(`Take ${dose}`);
  if (med.ingestionMode) parts.push(`by ${med.ingestionMode.toLowerCase()}`);
  if (med.frequency) parts.push(med.frequency.toLowerCase());
  return parts.length > 0 ? parts.join(" ") : med.dosage || "—";
};

const getInitials = (name) => {
  if (!name) return "?";
  const words = name.trim().split(/\s+/);
  if (words.length === 1) return words[0].charAt(0).toUpperCase();
  return (words[0].charAt(0) + words[1].charAt(0)).toUpperCase();
};

const Logo = ({ facilityPhoto, facilityName }) => {
  if (facilityPhoto && facilityPhoto !== "undefined" && facilityPhoto !== "null") {
    try { return <Image src={facilityPhoto} style={s.coverLogoImg} />; } catch (e) { /* fallback */ }
  }
  return (
    <View style={s.coverLogoCircle}>
      <Text style={s.coverLogoText}>{getInitials(facilityName)}</Text>
    </View>
  );
};

const SmallLogo = ({ facilityPhoto, facilityName }) => {
  if (facilityPhoto && facilityPhoto !== "undefined" && facilityPhoto !== "null") {
    try { return <Image src={facilityPhoto} style={s.contLogoImg} />; } catch (e) { /* fallback */ }
  }
  return (
    <View style={s.contLogo}>
      <Text style={s.contLogoText}>{getInitials(facilityName)}</Text>
    </View>
  );
};

/* ─── Footer ─── */
const Footer = ({ facilityName, facilityPhoto, pageLabel }) => (
  <View style={s.footer} fixed>
    <View style={{ flexDirection: "row", alignItems: "center", gap: 6 }}>
      <View style={{ width: 16, height: 16, borderRadius: 8, backgroundColor: C.amber, justifyContent: "center", alignItems: "center" }}>
        <Text style={{ fontSize: 7, fontWeight: "bold", color: C.white }}>{getInitials(facilityName)}</Text>
      </View>
      <View>
        <Text style={s.footerBold}>Generated using Ogiso EHR</Text>
        <Text style={s.footerText}>{facilityName || "Ogiso"} · Confidential</Text>
      </View>
    </View>
    <View style={s.footerRight}>
      <Text style={s.footerBold} render={({ pageNumber }) => `Page ${pageNumber}`} />
      {pageLabel && <Text style={s.footerText}>{pageLabel}</Text>}
    </View>
  </View>
);

/* ─── Continuation header (pages 2+) ─── */
const ContinuationHeader = ({ facilityName, facilityPhoto, clientName, asOfDate }) => (
  <View style={s.contHeader} fixed>
    <View style={s.contLeft}>
      <SmallLogo facilityPhoto={facilityPhoto} facilityName={facilityName} />
      <View>
        <Text style={s.contFacility}>{facilityName}</Text>
        <Text style={s.contSub}>Current Medication List</Text>
      </View>
    </View>
    <View style={s.contRight}>
      <Text style={s.contClient}>{clientName}</Text>
      <Text style={s.contMeta} render={({ pageNumber }) => `As of ${asOfDate}  ·  Page ${pageNumber}`} />
    </View>
  </View>
);

/* ═══ MEDICATION CARD — single-row horizontal layout ═══ */
const MedCard = ({ med }) => {
  const isDisc = !!med.discontinued;
  const isPRN = !isDisc && ((med.frequency || "").toLowerCase().includes("prn") || (med.frequency || "").toLowerCase().includes("as needed"));
  const stripeStyle = isDisc ? s.stripeDisc : (isPRN ? s.stripePRN : s.stripeActive);
  const dotColor = isDisc ? "#D1D5DB" : "#10B981";

  const doseStr = [med.dosage, med.unit].filter(Boolean).join(" ") || "—";
  const startDate = med.medicationDate ? moment(med.medicationDate).format("MM/DD/YYYY") : "—";
  const endDate = med.discontinuedOn ? moment(med.discontinuedOn).format("MM/DD/YYYY") : null;
  const freq = med.frequency || "—";

  // Badge color for type
  const typeBg = isDisc ? "#F3F4F6" : "#EFF6FF";
  const typeColor = isDisc ? "#6B7280" : "#1E3A8A";

  if (isDisc) {
    return (
      <View style={[s.medCard, { opacity: 0.7 }]} wrap={false}>
        <View style={[s.medStripe, stripeStyle]} />
        <View style={s.medRow}>
          <View style={[s.medDot, { backgroundColor: dotColor }]} />
          <View style={s.medNameBlock}>
            <Text style={s.discMedName}>{med.medicationName || "Unknown"}</Text>
            <Text style={s.medDosage}>{[med.dosage, med.unit, med.dosageForm].filter(Boolean).join(" ")}</Text>
          </View>
          {med.medicationType ? (
            <View style={[s.medTypeBadge, { backgroundColor: typeBg }]}>
              <Text style={[s.medTypeBadgeText, { color: typeColor }]}>{med.medicationType}</Text>
            </View>
          ) : null}
          <View style={s.medSpacer} />
          <View style={s.medInfoCol}>
            <Text style={s.medInfoLabel}>Ended</Text>
            <Text style={s.medInfoValueRed}>{endDate || "—"}</Text>
          </View>
          <View style={{ width: 100, flexShrink: 0 }}>
            <Text style={s.medInfoLabel}>Reason</Text>
            <Text style={s.medInfoValueMuted}>{med.discontinueReason || "—"}</Text>
          </View>
          <View style={[s.freqBadge, s.freqBadgeDisc]}>
            <Text style={s.freqBadgeTextDisc}>DISC</Text>
          </View>
        </View>
      </View>
    );
  }

  return (
    <View style={s.medCard} wrap={false}>
      <View style={[s.medStripe, stripeStyle]} />
      <View style={s.medRow}>
        <View style={[s.medDot, { backgroundColor: dotColor }]} />
        <View style={s.medNameBlock}>
          <Text style={s.medName}>{med.medicationName || "Unknown"}</Text>
          <Text style={s.medDosage}>{[med.dosage, med.unit, med.dosageForm].filter(Boolean).join(" ")}</Text>
        </View>
        {med.medicationType ? (
          <View style={[s.medTypeBadge, { backgroundColor: typeBg }]}>
            <Text style={[s.medTypeBadgeText, { color: typeColor }]}>{med.medicationType}</Text>
          </View>
        ) : null}
        <View style={s.medSpacer} />
        <View style={s.medInfoCol}>
          <Text style={s.medInfoLabel}>Dose</Text>
          <Text style={s.medInfoValue}>{doseStr}</Text>
        </View>
        <View style={s.medInfoCol}>
          <Text style={s.medInfoLabel}>Route</Text>
          <Text style={s.medInfoValue}>{med.ingestionMode || "Oral"}</Text>
        </View>
        <View style={s.medInfoCol}>
          <Text style={s.medInfoLabel}>Start</Text>
          <Text style={s.medInfoValue}>{startDate}</Text>
        </View>
        <View style={[s.freqBadge, s.freqBadgeActive]}>
          <Text style={s.freqBadgeText}>{freq}</Text>
        </View>
      </View>
    </View>
  );
};

/* ═══════════════ MAIN DOCUMENT ═══════════════ */
const MedicationListPDF = ({
  clientData,
  medications = [],
  facilityName = "Facility",
  facilityPhoto,
}) => {
  const now = moment();
  const asOfDate = now.format("MMMM D, YYYY");
  const asOfShort = now.format("MM/DD/YYYY");
  const generatedTime = now.format("MM/DD/YYYY [at] h:mm A");

  const clientName = clientData?.name || "Client";
  const form1 = clientData?.form1 || {};
  const form2 = clientData?.form2 || {};

  const activeMeds = medications.filter((m) => !m.discontinued && !(m.frequency || "").toLowerCase().includes("prn") && !(m.frequency || "").toLowerCase().includes("as needed"));
  const prnMeds = medications.filter((m) => !m.discontinued && ((m.frequency || "").toLowerCase().includes("prn") || (m.frequency || "").toLowerCase().includes("as needed")));
  const discMeds = medications.filter((m) => m.discontinued);
  const allActive = [...activeMeds, ...prnMeds];

  const dob = form1.dob ? moment(form1.dob) : null;
  const age = dob ? `${dob.format("MM/DD/YYYY")} (${now.diff(dob, "years")} years)` : "—";
  const allergies = form1.knownAllergies?.length ? form1.knownAllergies.join(", ") : form1.allergies || "None known";
  const physician = form2.pcpName || form1.attendingPhysician || form1.physician || "—";
  const pharmacy = form2.pharmacyName || "—";

  const admissionDate = form1.admissionDate
    ? moment(form1.admissionDate).format("MM/DD/YYYY")
    : form1.admissiondate
      ? moment(form1.admissiondate).format("MM/DD/YYYY")
      : "—";

  // helpers
  const initials = (clientName || "?").split(" ").map(n => n?.[0] || "").join("").slice(0, 2);
  const guardian = form2?.guardianName || form1?.guardianName || null;
  const guardianPhone = form2?.guardianPhone || form1?.guardianPhone || "";
  const guardianEmail = form2?.guardianEmail || form1?.guardianEmail || "";
  const dietaryRestrictions = form1?.dietaryRestrictions?.length ? form1.dietaryRestrictions.join(", ") : null;

  // Build active med names for summary card
  const activeMedNames = allActive.map(m => m.medicationName).filter(Boolean);

  /* info row component */
  const InfoRow = ({ label, value, valueStyle }) => (
    <View style={{ flexDirection: "row", justifyContent: "space-between", alignItems: "baseline", paddingVertical: 4, borderBottomWidth: 0.5, borderBottomColor: "#f0eeea" }}>
      <Text style={{ fontSize: 9, color: "#888" }}>{label}</Text>
      <Text style={[{ fontSize: 9, fontWeight: "medium", color: "#1a1a1a", textAlign: "right" }, valueStyle]}>{value}</Text>
    </View>
  );

  /* card header */
  const CardHeader = ({ title, bgColor }) => (
    <View style={{ padding: "7px 12px", backgroundColor: "#f7f6f2", borderBottomWidth: 0.5, borderBottomColor: "#e4e0d8", flexDirection: "row", alignItems: "center", gap: 6 }}>
      <View style={{ width: 16, height: 16, borderRadius: 4, backgroundColor: bgColor || "#E6F1FB", justifyContent: "center", alignItems: "center" }}>
        <Text style={{ fontSize: 7, fontWeight: "bold", color: "#555" }}>·</Text>
      </View>
      <Text style={{ fontSize: 8, fontWeight: "bold", textTransform: "uppercase", letterSpacing: 0.5, color: "#1a1a1a" }}>{title}</Text>
    </View>
  );

  /* contact item */
  const ContactItem = ({ role, name, detail, color }) => (
    <View style={{ flexDirection: "row", gap: 8, paddingVertical: 6, borderBottomWidth: 0.5, borderBottomColor: "#f0eeea" }}>
      <View style={{ width: 24, height: 24, borderRadius: 12, backgroundColor: color || "#EEEDFE", justifyContent: "center", alignItems: "center" }}>
        <Text style={{ fontSize: 7, fontWeight: "bold", color: "#534AB7" }}>{(name || "?").split(" ").map(n => n?.[0]).join("").slice(0, 2)}</Text>
      </View>
      <View>
        <Text style={{ fontSize: 7, color: "#888", marginBottom: 1 }}>{role}</Text>
        <Text style={{ fontSize: 9, fontWeight: "medium", color: "#1a1a1a" }}>{name}</Text>
        {detail ? <Text style={{ fontSize: 8, color: "#777", marginTop: 1 }}>{detail}</Text> : null}
      </View>
    </View>
  );

  return (
    <Document>
      {/* ═══ PAGE 1 — COVER ═══ */}
      <Page size="A4" style={[s.page, { backgroundColor: "#fff", padding: 0 }]}>

        {/* ── SINGLE HEADER: dark navy with all client info ── */}
        <View style={{ backgroundColor: "#1e2533", padding: "28px 36px 22px" }}>
          {/* Top line: facility + report type + date */}
          <View style={{ flexDirection: "row", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
            <View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
              <Logo facilityPhoto={facilityPhoto} facilityName={facilityName} />
              <View>
                <Text style={{ fontSize: 11, fontWeight: "bold", color: "rgba(255,255,255,0.7)" }}>{facilityName}</Text>
                <Text style={{ fontSize: 8, color: "rgba(255,255,255,0.35)" }}>Current Medication List</Text>
              </View>
            </View>
            <Text style={{ fontSize: 8, color: "rgba(255,255,255,0.35)" }}>Generated {generatedTime}</Text>
          </View>

          {/* Client name row */}
          <View style={{ flexDirection: "row", alignItems: "center", marginBottom: 4 }}>
            <Text style={{ fontSize: 26, fontWeight: "bold", color: "#fff", letterSpacing: -0.4 }}>{clientName}</Text>
            {form1.levelofcare ? (
              <View style={{ backgroundColor: "rgba(230,168,23,0.15)", borderWidth: 0.5, borderColor: "rgba(230,168,23,0.3)", borderRadius: 12, paddingVertical: 2, paddingHorizontal: 8, marginLeft: 10 }}>
                <Text style={{ fontSize: 8, fontWeight: "bold", color: "#E6A817" }}>{form1.levelofcare}</Text>
              </View>
            ) : null}
          </View>
          <Text style={{ fontSize: 10, color: "rgba(255,255,255,0.45)", marginBottom: 18 }}>Admitted {admissionDate}  ·  As of {asOfDate}</Text>

          {/* Stats row */}
          <View style={{ flexDirection: "row", borderTopWidth: 0.5, borderTopColor: "rgba(255,255,255,0.1)", paddingTop: 14 }}>
            {[
              { label: "Date of birth", value: dob ? dob.format("MM/DD/YYYY") : "—", color: "#fff" },
              { label: "Sex", value: form1.gender || "—", color: "#fff" },
              { label: "Height / Weight", value: `${form1.height || "—"}  /  ${form1.weight ? `${form1.weight} lbs` : "—"}`, color: "#fff" },
              { label: "Active medications", value: String(allActive.length), color: "#E6A817" },
              { label: "Allergies", value: allergies, color: allergies !== "None known" ? "#FCA5A5" : "#fff" },
            ].map((stat, i) => (
              <View key={i} style={{ flex: 1, paddingHorizontal: i === 0 ? 0 : 12, paddingLeft: i === 0 ? 0 : 12, borderRightWidth: i < 4 ? 0.5 : 0, borderRightColor: "rgba(255,255,255,0.1)" }}>
                <Text style={{ fontSize: 7, color: "rgba(255,255,255,0.35)", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 2 }}>{stat.label}</Text>
                <Text style={{ fontSize: 11, fontWeight: "bold", color: stat.color }}>{stat.value}</Text>
              </View>
            ))}
          </View>
        </View>

        {/* ── BODY ── */}
        <View style={{ padding: "22px 36px", flex: 1 }}>

          {/* Allergy alert */}
          {allergies !== "None known" ? (
            <View style={{ backgroundColor: "#FCEBEB", borderWidth: 0.5, borderColor: "#F09595", borderRadius: 8, padding: "8px 12px", flexDirection: "row", alignItems: "flex-start", gap: 8, marginBottom: 16 }}>
              <View style={{ width: 16, height: 16, borderRadius: 8, backgroundColor: "#A32D2D", justifyContent: "center", alignItems: "center", flexShrink: 0, marginTop: 1 }}>
                <Text style={{ fontSize: 9, fontWeight: "bold", color: "#fff" }}>!</Text>
              </View>
              <Text style={{ fontSize: 9, color: "#7F1D1D", lineHeight: 1.5, flex: 1 }}>
                <Text style={{ fontWeight: "bold" }}>Allergy alert: </Text>{allergies}
                {dietaryRestrictions ? <Text>  ·  <Text style={{ fontWeight: "bold" }}>Dietary restrictions: </Text>{dietaryRestrictions}</Text> : null}
                {"  ·  Verify all medications and ingredients before administration."}
              </Text>
            </View>
          ) : null}

          {/* Med summary cards */}
          <View style={{ flexDirection: "row", gap: 10, marginBottom: 18 }}>
            <View style={{ flex: 1, borderWidth: 0.5, borderColor: "#e4e0d8", borderRadius: 10, padding: 14, alignItems: "center" }}>
              <Text style={{ fontSize: 28, fontWeight: "bold", color: "#10B981", marginBottom: 3 }}>{activeMeds.length + prnMeds.length}</Text>
              <Text style={{ fontSize: 8, fontWeight: "bold", textTransform: "uppercase", letterSpacing: 0.5, color: "#888", marginBottom: 4 }}>Active</Text>
              <Text style={{ fontSize: 8, color: "#aaa", textAlign: "center", lineHeight: 1.4 }}>{activeMedNames.length > 0 ? activeMedNames.slice(0, 4).join(" · ") : "None"}{activeMedNames.length > 4 ? ` +${activeMedNames.length - 4} more` : ""}</Text>
            </View>
            <View style={{ flex: 1, borderWidth: 0.5, borderColor: "#e4e0d8", borderRadius: 10, padding: 14, alignItems: "center" }}>
              <Text style={{ fontSize: 28, fontWeight: "bold", color: "#E6A817", marginBottom: 3 }}>{prnMeds.length}</Text>
              <Text style={{ fontSize: 8, fontWeight: "bold", textTransform: "uppercase", letterSpacing: 0.5, color: "#888", marginBottom: 4 }}>PRN</Text>
              <Text style={{ fontSize: 8, color: "#aaa", textAlign: "center", lineHeight: 1.4 }}>{prnMeds.length > 0 ? prnMeds.map(m => m.medicationName).join(" · ") : "No PRN medications on file"}</Text>
            </View>
            <View style={{ flex: 1, borderWidth: 0.5, borderColor: "#e4e0d8", borderRadius: 10, padding: 14, alignItems: "center" }}>
              <Text style={{ fontSize: 28, fontWeight: "bold", color: "#9CA3AF", marginBottom: 3 }}>{discMeds.length}</Text>
              <Text style={{ fontSize: 8, fontWeight: "bold", textTransform: "uppercase", letterSpacing: 0.5, color: "#888", marginBottom: 4 }}>Discontinued</Text>
              <Text style={{ fontSize: 8, color: "#aaa", textAlign: "center", lineHeight: 1.4 }}>{discMeds.length > 0 ? "Previously active, no longer administered" : "None discontinued"}</Text>
            </View>
          </View>

          {/* Two-column grid: Resident details + Care team / Legend */}
          <View style={{ flexDirection: "row", gap: 16 }}>
            {/* Left: Resident details card */}
            <View style={{ flex: 1, borderWidth: 0.5, borderColor: "#e4e0d8", borderRadius: 10, overflow: "hidden" }}>
              <CardHeader title="Resident details" bgColor="#E6F1FB" />
              <View style={{ padding: "10px 12px" }}>
                <InfoRow label="Full name" value={clientName} />
                <InfoRow label="Date of birth" value={age} />
                <InfoRow label="Sex" value={form1.gender || "—"} />
                <InfoRow label="Height / Weight" value={`${form1.height || "—"}  /  ${form1.weight ? `${form1.weight} lbs` : "—"}`} />
                <InfoRow label="Admission date" value={admissionDate} />
                <InfoRow label="Level of care" value={form1.levelofcare || "—"} />
                <InfoRow label="Allergies" value={allergies} valueStyle={allergies !== "None known" ? { color: "#A32D2D", fontWeight: "bold" } : {}} />
                {dietaryRestrictions ? <InfoRow label="Dietary restrictions" value={dietaryRestrictions} valueStyle={{ color: "#0F6E56" }} /> : null}
              </View>
            </View>

            {/* Right: Care team + Legend */}
            <View style={{ flex: 1, gap: 12 }}>
              <View style={{ borderWidth: 0.5, borderColor: "#e4e0d8", borderRadius: 10, overflow: "hidden" }}>
                <CardHeader title="Care team" bgColor="#EAF3DE" />
                <View style={{ padding: "8px 12px" }}>
                  {guardian ? <ContactItem role="Guardian" name={guardian} detail={[guardianPhone, guardianEmail].filter(Boolean).join("  ·  ") || null} color="#EEEDFE" /> : null}
                  <ContactItem role="Primary care physician" name={physician} color="#E6F1FB" />
                  <ContactItem role="Pharmacy" name={pharmacy} color="#F1EFE8" />
                </View>
              </View>

              <View style={{ borderWidth: 0.5, borderColor: "#e4e0d8", borderRadius: 10, overflow: "hidden" }}>
                <CardHeader title="Status legend" bgColor="#FAEEDA" />
                <View style={{ padding: "8px 12px" }}>
                  {[
                    { color: "#10B981", label: "Active", desc: "currently prescribed and administered" },
                    { color: "#E6A817", label: "PRN", desc: "administered as needed" },
                    { color: "#9CA3AF", label: "Discontinued", desc: "no longer active" },
                  ].map((item, i) => (
                    <View key={i} style={{ flexDirection: "row", alignItems: "center", gap: 6, marginBottom: 5 }}>
                      <View style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: item.color }} />
                      <Text style={{ fontSize: 9, color: "#555" }}><Text style={{ fontWeight: "bold" }}>{item.label}</Text> — {item.desc}</Text>
                    </View>
                  ))}
                </View>
              </View>
            </View>
          </View>

        </View>

        <Footer facilityName={facilityName} facilityPhoto={facilityPhoto} pageLabel="Medication list continues on next page" />
      </Page>

      {/* ═══ PAGE 2+ — MEDICATION CARDS ═══ */}
      <Page size="A4" style={s.page} wrap>
        {/* Continuation header (fixed) */}
        <View style={{ flexDirection: "row", justifyContent: "space-between", alignItems: "center", paddingBottom: 8, borderBottomWidth: 1.5, borderBottomColor: "#1C1917", marginBottom: 14, marginTop: -10 }} fixed>
          <View style={{ flexDirection: "row", alignItems: "center", gap: 6 }}>
            <SmallLogo facilityPhoto={facilityPhoto} facilityName={facilityName} />
            <View>
              <Text style={{ fontSize: 10, fontWeight: "bold", color: "#1C1917" }}>{facilityName}</Text>
              <Text style={{ fontSize: 7, color: "#9C9690" }}>Current Medication List</Text>
            </View>
          </View>
          <View style={{ alignItems: "flex-end" }}>
            <Text style={{ fontSize: 10, fontWeight: "bold", color: "#1C1917" }}>{clientName}</Text>
            <Text style={{ fontSize: 7, color: "#9C9690" }} render={({ pageNumber }) => `As of ${asOfShort}  ·  Page ${pageNumber}`} />
          </View>
        </View>

        {/* Active meds section */}
        {allActive.length > 0 && (
          <>
            <View style={s.sectionHead}>
              <Text style={s.sectionTitle}>Active medications</Text>
              <Text style={[s.sectionBadge, s.sectionBadgeActive]}>{allActive.length} medication{allActive.length !== 1 ? "s" : ""}</Text>
            </View>
            {allActive.map((med, i) => (
              <MedCard key={`active-${i}`} med={med} />
            ))}
          </>
        )}

        {/* Discontinued section */}
        {discMeds.length > 0 && (
          <>
            <View style={{ marginTop: 16, marginBottom: 10, alignItems: "center" }}>
              <View style={{ borderBottomWidth: 1, borderBottomColor: "#E4E0D8", width: "100%", position: "absolute", top: "50%" }} />
              <Text style={{ backgroundColor: "#F5F3EE", paddingHorizontal: 10, fontSize: 7, fontWeight: "bold", textTransform: "uppercase", letterSpacing: 0.6, color: "#9C9690" }}>Discontinued</Text>
            </View>
            <View style={s.sectionHead}>
              <Text style={s.sectionTitle}>Discontinued medications</Text>
              <Text style={{ fontSize: 7, color: "#9C9690" }}>No longer active</Text>
            </View>
            {discMeds.map((med, i) => (
              <MedCard key={`disc-${i}`} med={med} />
            ))}
          </>
        )}

        <Footer facilityName={facilityName} facilityPhoto={facilityPhoto} />
      </Page>
    </Document>
  );
};

export default MedicationListPDF;
